/**
 * ActionButton: the one button, as one family: a `variant` (the look) and a `size` (the geometry), each a single
 * typed enum, and a `span` that says how much of its row the button takes.
 *
 * The looks that wear a paint (the paint defaults to the secondary ink; a stratum that means something passes its
 * own, complying green for "fix", absence amber for a lux toggle):
 *  - quiet:   paint text and border on transparent (the viewer's verb buttons, dismissals, secondary actions);
 *  - filled:  the same on a 15% wash (a verb that is the row's point), which a `pressed` quiet button also becomes;
 *  - brand:   teal fill, on-brand text: the ONE primary action a screen has, and never more than one;
 *  - bare:    no border, left-aligned, full width: a row in a list that acts (a catalog kind, a child rung).
 * The looks that are the cockpit's verbs (Figma 00, 3, 4, 2:2):
 *  - solid:   strong ink fill, surface text: the one primary of a row;
 *  - outline: the hairline-bordered alternative;
 *  - ghost:   the quiet retraction, no border;
 *  - accept:  the complying green (Accept, Approve) and deny the red of the deny screen, each with its own
 *             palette tint for the keyboard letter beside the label (4.5:1 on the fill, pinned in brand-ink.test.ts).
 * Sizes: sm (24 tall) and md (32 tall) for the paint looks; compact, roomy, bar (a bar's verbs) and block (the rule
 * screen's 44px verdicts, 36 for a ghost) for the verbs; a verb with no size is the cockpit's roomy one.
 * `keyHint` is the keyboard letter that does the same thing, hidden from assistive technology so the name is the
 * label alone. `isPending` is busy, not disabled-looking: held here (presses ignored) because react-aria's own
 * pending spinner is a role=img with no text alternative, which axe refuses. `pressed` is a toggle's state
 * (aria-pressed).
 */
import type { CSSProperties, ReactNode } from 'react';
import { Button, type ButtonProps, type ButtonRenderProps } from 'react-aria-components';
import { BRAND, BUTTON, CONTROL, DIM, RADIUS, SPACE, TEXT, WASH_ALPHA, WEIGHT, tint, usePalette, type Hex, type Palette } from '../theme';

export type ActionVariant = 'quiet' | 'filled' | 'brand' | 'bare' | 'solid' | 'outline' | 'ghost' | 'accept' | 'deny';
export type ActionSize = 'sm' | 'md' | 'compact' | 'roomy' | 'bar' | 'block';

/** How much of its row a button takes: its own width, the row's spare width, all of it, or a width of its own. */
export type ActionSpan =
  | { readonly kind: 'hug' }
  | { readonly kind: 'grow' }
  | { readonly kind: 'fill' }
  | { readonly kind: 'fixed'; readonly width: number };

export const HUG: ActionSpan = { kind: 'hug' };
export const GROW: ActionSpan = { kind: 'grow' };
export const FILL: ActionSpan = { kind: 'fill' };

export function focusRing(p: Palette): string {
  return `0 0 0 2px ${p.surface}, 0 0 0 4px ${BRAND.teal}`;
}

/** Whether a look is one of the cockpit's verbs, which have no paint of their own to take. */
function isVerb(variant: ActionVariant): boolean {
  return variant === 'solid' || variant === 'outline' || variant === 'ghost' || variant === 'accept' || variant === 'deny';
}

/** The size a button is when none is said: a paint look is a control tall, a verb the cockpit's roomy one. */
function sizeOf(variant: ActionVariant, size: ActionSize | undefined): ActionSize {
  return size ?? (isVerb(variant) ? 'roomy' : 'md');
}

/** The padding or height, radius and type a size sets. */
function geometry(variant: ActionVariant, size: ActionSize): CSSProperties {
  switch (size) {
    case 'sm':
      return { height: CONTROL.heightSm, padding: `0 ${CONTROL.padXSm}px`, borderRadius: RADIUS.sm, fontSize: TEXT.caption, lineHeight: 1 };
    case 'md':
      return { height: CONTROL.height, padding: `0 ${CONTROL.padX}px`, borderRadius: RADIUS.sm, fontSize: TEXT.control, lineHeight: 1 };
    case 'compact':
      return { padding: `${BUTTON.padYCompact}px ${BUTTON.padXCompact}px`, borderRadius: RADIUS.button, fontSize: TEXT.control };
    case 'roomy':
      return { padding: `${CONTROL.padYBlock}px ${CONTROL.padX}px`, borderRadius: RADIUS.button, fontSize: TEXT.control };
    case 'bar':
      return { padding: `${BUTTON.padYBar}px ${BUTTON.padXBar}px`, borderRadius: RADIUS.button, fontSize: TEXT.body };
    case 'block':
      return {
        padding: `${BUTTON.padYBar}px ${BUTTON.padXBar}px`,
        minHeight: variant === 'ghost' ? CONTROL.blockGhost : CONTROL.block,
        borderRadius: RADIUS.button,
        fontSize: TEXT.body,
      };
  }
}

/** What a look wears: its ground at rest, hovered and pressed, its ink, its border, its weight, and its hint's colour. */
interface Look {
  readonly ground: string;
  readonly hover: string;
  readonly press: string;
  readonly ink: string;
  readonly border: string;
  readonly weight: number;
  readonly hint: string;
}

function lookOf(p: Palette, variant: ActionVariant, paint: Hex, pressed: boolean): Look {
  switch (variant) {
    case 'quiet':
    case 'filled': {
      const washed = variant === 'filled' || pressed;
      return { ground: washed ? tint(paint, WASH_ALPHA.rest) : 'transparent', hover: tint(paint, WASH_ALPHA.hover), press: tint(paint, WASH_ALPHA.press), ink: paint, border: paint, weight: WEIGHT.medium, hint: paint };
    }
    case 'brand':
      return { ground: BRAND.teal, hover: BRAND.tealHover, press: BRAND.tealHover, ink: BRAND.onTeal, border: BRAND.teal, weight: WEIGHT.heavy, hint: BRAND.onTeal };
    case 'bare':
      return { ground: 'transparent', hover: tint(paint, WASH_ALPHA.hover), press: tint(paint, WASH_ALPHA.press), ink: p.ink, border: 'transparent', weight: WEIGHT.regular, hint: p.inkMuted };
    case 'solid':
      return { ground: p.inkStrong, hover: p.inkStrong, press: p.inkStrong, ink: p.surface, border: 'transparent', weight: WEIGHT.strong, hint: p.inkFaint };
    case 'accept':
      return { ground: p.complying, hover: p.complying, press: p.complying, ink: p.surface, border: 'transparent', weight: WEIGHT.strong, hint: p.acceptHint };
    case 'deny':
      return { ground: p.red, hover: p.red, press: p.red, ink: p.surface, border: 'transparent', weight: WEIGHT.strong, hint: p.denyHint };
    case 'outline': {
      const lifted = pressed ? p.surfaceRaised : p.surface;
      return { ground: lifted, hover: p.surfaceRaised, press: tint(p.ink, WASH_ALPHA.inkPress), ink: p.ink, border: p.hairline, weight: WEIGHT.medium, hint: p.inkMuted };
    }
    case 'ghost':
      return { ground: pressed ? p.surfaceRaised : 'transparent', hover: p.surfaceRaised, press: tint(p.ink, WASH_ALPHA.inkPress), ink: p.inkMuted, border: 'transparent', weight: WEIGHT.regular, hint: p.inkMuted };
  }
}

/** The width a span gives a button. */
function spanStyle(span: ActionSpan, variant: ActionVariant): CSSProperties {
  if (variant === 'bare') return { display: 'flex', width: '100%', textAlign: 'left', justifyContent: 'flex-start' };
  switch (span.kind) {
    case 'hug':
      return {};
    case 'grow':
      return { flex: '1 0 0', minWidth: 1 };
    case 'fill':
      return { display: 'flex', width: '100%' };
    case 'fixed':
      return { width: span.width, flexShrink: 0 };
  }
}

function style(
  p: Palette,
  paint: Hex,
  variant: ActionVariant,
  size: ActionSize,
  span: ActionSpan,
  pressed: boolean,
  held: boolean,
  s: ButtonRenderProps,
): CSSProperties {
  const look = lookOf(p, variant, paint, pressed);
  const lifted = s.isPressed ? look.press : s.isHovered || (pressed && (variant === 'outline' || variant === 'ghost')) ? look.hover : look.ground;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: CONTROL.gapKey,
    boxSizing: 'border-box',
    font: 'inherit',
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    cursor: held ? 'progress' : s.isDisabled ? 'not-allowed' : 'pointer',
    opacity: s.isDisabled && !held ? DIM.disabled : 1,
    outline: 'none',
    boxShadow: s.isFocusVisible ? focusRing(p) : undefined,
    transition: 'background-color 0.15s ease',
    ...geometry(variant, variant === 'bare' ? 'sm' : size),
    ...(variant === 'bare' ? { padding: `0 ${SPACE.xs}px` } : {}),
    background: lifted,
    color: look.ink,
    border: `1px solid ${look.border}`,
    fontWeight: look.weight,
    ...spanStyle(span, variant),
  };
}

/** The keyboard letter's type: the caption on the sizes that carry a label of body or control weight, the micro on the small. */
function hintSize(size: ActionSize): number {
  return size === 'sm' || size === 'roomy' ? TEXT.micro : TEXT.caption;
}

export function ActionButton({
  variant = 'quiet',
  size,
  span = HUG,
  paint,
  keyHint,
  pressed,
  isPending,
  isDisabled,
  children,
  ...rest
}: Omit<ButtonProps, 'className' | 'style' | 'children'> & {
  variant?: ActionVariant;
  size?: ActionSize;
  span?: ActionSpan;
  paint?: Hex;
  /** The keyboard letter that does the same thing, shown beside the label. */
  keyHint?: string;
  /** A toggle's state, announced as aria-pressed; absent, the button is no toggle. */
  pressed?: boolean;
  isPending?: boolean;
  children: ReactNode;
}) {
  const p = usePalette();
  const own = paint ?? p.inkSecondary;
  const sized = sizeOf(variant, size);
  const hint = lookOf(p, variant, own, pressed === true).hint;
  return (
    <Button
      {...rest}
      isDisabled={isDisabled === true || isPending === true}
      aria-pressed={pressed}
      style={(s) => style(p, own, variant, sized, span, pressed === true, isPending === true, s)}
    >
      {children}
      {keyHint !== undefined && (
        <span aria-hidden="true" style={{ color: hint, fontSize: hintSize(sized), fontWeight: WEIGHT.regular }}>
          {keyHint}
        </span>
      )}
    </Button>
  );
}
